import { Box, Link, Paper, Typography, useColorScheme } from '@mui/material';
import { RainbowKitProvider } from '@rainbow-me/rainbowkit';
import { memo, type ReactNode, useMemo } from 'react';
import type { Chain } from 'viem';
import { monadTestnet } from 'viem/chains';
import { WagmiProvider } from 'wagmi';
import { ConfirmationDialog } from '@/components/dialogs';
import { ErrorBoundary } from '@/components/error_boundary.tsx';
import { darkRainbow, lightRainbow } from '@/rainbowTheme.ts';
import { DISCORD_URL, RESCUE_URL, TWITTER_URL } from '@/utils/externalLinks';
import { AccountDialog } from '../components/dialogs/AccountDialog';
import { rainbowConfig } from '../components/rainbow/rainbow_config';
import { Web3Watchers } from '../components/Web3Watchers';
import { DepositCollateralDialog, OneClickTradingDialog, WithdrawCollateralDialog } from '../features/account';
import { AccessRequestDialog, GeoBlockDialog, TermsDialog } from '../features/auth';
import { AdjustMarginDialog } from '../features/trading/adjust_margin_dialog';
import useAppConfigStore from '../store/rootStore.ts';
import { FunBridgeGate } from './fun_bridge/fun_bridge_gate';

const Web3ProvidersComponent = ({ children }: { children: ReactNode }) => {
  const { mode } = useColorScheme();
  const config = useAppConfigStore((state) => state.config);
  const configStatus = useAppConfigStore((state) => state.configStatus);

  const wagmiConfig = useMemo(() => {
    if (!config?.chain) {
      return undefined;
    }
    if (config.chain.id && config.chain.rpcUrlsList?.length) {
      return rainbowConfig(config.chain as Chain);
    }
    return rainbowConfig(monadTestnet);
  }, [config]);

  if (!config || !wagmiConfig) {
    if (config && !config.chain) {
      console.error('Default chain is not provided');
    }
    if (configStatus === 'error') {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100dvh',
            p: 3,
          }}
        >
          <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Can't reach Perpl right now
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              We couldn't load app configuration. Please try refreshing the page — this is usually temporary.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              If the issue persists and you need to manage positions or withdraw funds, you can use{' '}
              <Link href={RESCUE_URL} target="_blank" rel="noopener noreferrer" underline="always">
                Perpl Rescue
              </Link>
              .
            </Typography>
            <Typography variant="body2" color="text.secondary">
              For status updates, follow us on{' '}
              <Link href={DISCORD_URL} target="_blank" rel="noopener noreferrer" underline="always">
                Discord
              </Link>{' '}
              or{' '}
              <Link href={TWITTER_URL} target="_blank" rel="noopener noreferrer" underline="always">
                X
              </Link>
              .
            </Typography>
          </Paper>
        </Box>
      );
    }
    return null;
  }

  const content = (
    <>
      {children}
      <ErrorBoundary message="Wallet error. Please reload.">
        <Web3Watchers />
        <TermsDialog></TermsDialog>
        <AccessRequestDialog></AccessRequestDialog>
        <GeoBlockDialog />

        <OneClickTradingDialog></OneClickTradingDialog>
        <DepositCollateralDialog></DepositCollateralDialog>
        <WithdrawCollateralDialog></WithdrawCollateralDialog>
        <AdjustMarginDialog></AdjustMarginDialog>
        <ConfirmationDialog />
        <AccountDialog />
      </ErrorBoundary>
    </>
  );

  return (
    <ErrorBoundary>
      <WagmiProvider config={wagmiConfig}>
        <RainbowKitProvider theme={mode === 'dark' ? darkRainbow : lightRainbow}>
          {content}
          <FunBridgeGate />
        </RainbowKitProvider>
      </WagmiProvider>
    </ErrorBoundary>
  );
};

// TODO make a decision if manually manage dependant components, or hide everything while loading config
export const Web3Providers = memo(Web3ProvidersComponent, (prevProps, nextProps) => {
  return true;
});
